import type { FC, FormEvent, ReactNode } from 'react'

import { useState } from 'react'

import { cn } from '@/lib/utils'

type Status = 'idle' | 'loading' | 'done' | 'error'

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export const NewsletterForm: FC = (): ReactNode => {
    const [email, setEmail] = useState('')
    const [status, setStatus] = useState<Status>('idle')
    const [message, setMessage] = useState('')

    const onSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
        e.preventDefault()

        const value = email.trim()

        if (!EMAIL_PATTERN.test(value)) {
            setStatus('error')
            setMessage('Enter a valid email address.')

            return
        }

        setStatus('loading')
        setMessage('')

        try {
            const response = await fetch('/api/subscribe', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: value })
            })

            const data = await response.json()

            if (!response.ok) {
                setStatus('error')
                setMessage(typeof data?.message === 'string' ? data.message : 'Something went wrong. Try again.')

                return
            }

            setStatus('done')
            setMessage(data?.message ?? 'You are on the list.')
            setEmail('')
        } catch {
            setStatus('error')
            setMessage('Network problem. Try again in a moment.')
        }
    }

    return (
        <form onSubmit={onSubmit} className='w-full max-w-[420px]' noValidate>
            <label htmlFor='newsletter-email' className='sr-only'>
                Email address
            </label>

            <div className='flex items-center gap-2 rounded-[9px] border border-line bg-surface p-2'>
                <input
                    id='newsletter-email'
                    name='email'
                    type='email'
                    autoComplete='email'
                    placeholder='Your email'
                    value={email}
                    onChange={(e) => {
                        setEmail(e.target.value)

                        if (status !== 'idle') {
                            setStatus('idle')
                            setMessage('')
                        }
                    }}
                    aria-invalid={status === 'error'}
                    aria-describedby='newsletter-message'
                    className='h-[34px] w-full min-w-0 bg-transparent px-2 font-body text-[15px] text-ink outline-none placeholder:text-ink-mute'
                />

                <button
                    type='submit'
                    disabled={status === 'loading'}
                    className={cn(
                        'h-[34px] shrink-0 bg-[#fefff5] px-4 font-sans text-[13px] font-medium uppercase leading-[1em] tracking-[0.02em] text-[#0d0d0d]',
                        'transition-opacity duration-300 hover:opacity-90 disabled:opacity-60'
                    )}
                >
                    {status === 'loading' ? 'Sending' : 'Join'}
                </button>
            </div>

            <p
                id='newsletter-message'
                role='status'
                aria-live='polite'
                className={cn(
                    'mt-2 min-h-[18px] font-body text-[13px] leading-[1.4em]',
                    status === 'error' ? 'text-[#ff9b8a]' : 'text-ink-mute'
                )}
            >
                {message}
            </p>
        </form>
    )
}
